import React, { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Button,
  IconButton,
  Tooltip
} from '@mui/material'
import {
  TrendingUp,
  Assessment,
  Verified,
  Pending,
  Error as ErrorIcon,
  Refresh,
  Download,
  Visibility
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { format } from 'date-fns'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line
} from 'recharts'
import medicalCodingService from '../../../services/medicalCodingService'

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8']

const CodingDashboard = () => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  
  const [loading, setLoading] = useState(false)
  const [dashboardData, setDashboardData] = useState(null)
  const [statistics, setStatistics] = useState(null)

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    setLoading(true)
    try {
      const [dashboardResponse, statsResponse] = await Promise.all([
        medicalCodingService.getCodingDashboard(),
        medicalCodingService.getCodingStatistics()
      ])

      if (dashboardResponse.data.success) {
        setDashboardData(dashboardResponse.data.data)
      }

      if (statsResponse.data.success) {
        setStatistics(statsResponse.data.data)
      }
    } catch (error) {
      enqueueSnackbar('Failed to load dashboard data', { variant: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const StatCard = ({ title, value, subtitle, icon, color = 'primary', trend }) => (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h4" color={color} fontWeight={600}>
              {value}
            </Typography>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
            {trend && (
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingUp fontSize="small" color={trend > 0 ? 'success' : 'error'} />
                <Typography variant="caption" color={trend > 0 ? 'success.main' : 'error.main'}>
                  {trend > 0 ? '+' : ''}{trend}% from last week
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ color: `${color}.main` }}>
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  )

  if (loading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Loading Dashboard...
        </Typography>
        <LinearProgress />
      </Box>
    )
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          Medical Coding Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<Refresh />}
            onClick={loadDashboardData}
          >
            Refresh
          </Button>
          <Button
            startIcon={<Download />}
            variant="outlined"
          >
            Export Report
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Coded"
            value={dashboardData?.summary?.totalCoded || 0}
            subtitle="All time"
            icon={<Assessment fontSize="large" />}
            color="primary"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="This Week"
            value={dashboardData?.summary?.codedThisWeek || 0}
            subtitle="New codings"
            icon={<TrendingUp fontSize="large" />}
            color="success"
            trend={12}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Verified"
            value={dashboardData?.summary?.verifiedCount || 0}
            subtitle={`${dashboardData?.summary?.verificationRate || '0%'} rate`}
            icon={<Verified fontSize="large" />}
            color="info"
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending"
            value={dashboardData?.summary?.pendingVerification || 0}
            subtitle="Awaiting verification"
            icon={<Pending fontSize="large" />}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Alerts */}
      {dashboardData?.alerts && dashboardData.alerts.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Alerts & Notifications
            </Typography>
            {dashboardData.alerts.map((alert, index) => (
              <Alert 
                key={index}
                severity={alert.type}
                sx={{ mb: 1 }}
                action={
                  <Button size="small" onClick={() => navigate('/hospital/mrd/coding?status=QUERY_RAISED')}>
                    View
                  </Button>
                }
              >
                {alert.message}
              </Alert>
            ))}
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3}>
        {/* Recent Codings */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Recent Codings
                </Typography>
                <Button
                  size="small"
                  onClick={() => navigate('/hospital/mrd/coding')}
                >
                  View All
                </Button>
              </Box>
              
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>IPD No</TableCell>
                      <TableCell>Patient</TableCell>
                      <TableCell>Primary ICD</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dashboardData?.recentCodings?.slice(0, 5).map((coding) => (
                      <TableRow key={coding._id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>
                            {coding.mrdFile?.fileNumber}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {coding.mrdFile?.patientName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={coding.primaryDiagnosis?.icd10Code}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={coding.verificationStatus}
                            size="small"
                            color={
                              coding.verificationStatus === 'VERIFIED' ? 'success' :
                              coding.verificationStatus === 'PENDING' ? 'warning' : 'error'
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {format(new Date(coding.codedAt), 'MMM dd')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/hospital/mrd/coding/${coding.mrdFile._id}`)}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Coders */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Top Coders (This Month)
              </Typography>
              
              {dashboardData?.topCoders?.map((coder, index) => (
                <Box
                  key={coder.coderId}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    py: 1,
                    borderBottom: index < dashboardData.topCoders.length - 1 ? '1px solid #f0f0f0' : 'none'
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {coder.coderName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(coder.verificationRate * 100).toFixed(1)}% verified
                    </Typography>
                  </Box>
                  <Chip
                    label={coder.count}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                </Box>
              ))}
            </CardContent>
          </Card>
        </Grid>

        {/* Monthly Trends */}
        {statistics?.monthlyTrends && (
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Monthly Coding Trends
                </Typography>
                
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={statistics.monthlyTrends}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="period" />
                    <YAxis />
                    <RechartsTooltip />
                    <Line 
                      type="monotone" 
                      dataKey="coded" 
                      stroke="#8884d8" 
                      name="Coded"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="verified" 
                      stroke="#82ca9d" 
                      name="Verified"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* ICD Chapter Distribution */}
        {statistics?.byICDChapter && (
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  ICD Chapter Distribution
                </Typography>
                
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={statistics.byICDChapter.slice(0, 5)}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="count"
                    >
                      {statistics.byICDChapter.slice(0, 5).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* DRG Distribution */}
        {statistics?.drgDistribution && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Top DRG Groups
                </Typography>
                
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={statistics.drgDistribution.slice(0, 10)}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="drgCode" />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar dataKey="count" fill="#8884d8" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  )
}

export default CodingDashboard